import { ChannelPartner } from '@/types/channel-partner';
import { Company } from '@/types/company';
import { Team } from '@/types/teams';
import snakeCaseKeys from 'snakecase-keys';

// Multi-tenant endpoints to be merged with main piloteerApi
export const multiTenantEndpoints = {
  // Channel Partner endpoints
  getChannelPartners: {
    query: () => 'channel-partners',
    providesTags: ['ChannelPartner' as const],
  },
  getChannelPartnerById: {
    query: (uuid: string) => `channel-partners/${uuid}`,
    providesTags: (_result: unknown, _error: unknown, uuid: string) => [
      { type: 'ChannelPartner' as const, uuid },
    ],
  },
  createChannelPartner: {
    mutation: {
      query: (partner: Partial<ChannelPartner>) => ({
        url: 'channel-partners',
        method: 'POST',
        body: snakeCaseKeys(partner, { deep: true }),
      }),
      invalidatesTags: ['ChannelPartner' as const, 'Analytics' as const],
    },
  },
  updateChannelPartner: {
    mutation: {
      query: ({
        uuid,
        ...updates
      }: Partial<ChannelPartner> & { uuid: string }) => ({
        url: `channel-partners/${uuid}`,
        method: 'PATCH',
        body: snakeCaseKeys(updates, { deep: true }),
      }),
      invalidatesTags: (
        _result: unknown,
        _error: unknown,
        { uuid }: { uuid: string }
      ) => [{ type: 'ChannelPartner' as const, uuid }, 'Analytics' as const],
    },
  },
  getCompaniesByChannelPartner: {
    query: (channelPartnerUuid: string) =>
      `channel-partners/${channelPartnerUuid}/companies`,
    providesTags: ['Company' as const],
  },

  // Channel Partner Dashboard endpoints
  getChannelPartnerDashboard: {
    query: (params?: { startDate?: string; endDate?: string }) => {
      const queryParams = new URLSearchParams();
      if (params?.startDate) queryParams.append('start_date', params.startDate);
      if (params?.endDate) queryParams.append('end_date', params.endDate);
      const queryString = queryParams.toString();
      return queryString
        ? `channel-partners/dashboard?${queryString}`
        : 'channel-partners/dashboard';
    },
    providesTags: ['ChannelPartner' as const, 'Analytics' as const],
  },
  getChannelPartnerDashboardById: {
    query: (
      partnerUuid: string,
      params?: { startDate?: string; endDate?: string }
    ) => {
      const queryParams = new URLSearchParams();
      if (params?.startDate) queryParams.append('start_date', params.startDate);
      if (params?.endDate) queryParams.append('end_date', params.endDate);
      const queryString = queryParams.toString();
      return queryString
        ? `channel-partners/${partnerUuid}/dashboard?${queryString}`
        : `channel-partners/${partnerUuid}/dashboard`;
    },
    providesTags: (_result: unknown, _error: unknown, partnerUuid: string) => [
      { type: 'ChannelPartner' as const, uuid: partnerUuid },
      'Analytics' as const,
    ],
  },

  // Company endpoints
  getCompanies: {
    query: () => 'companies',
    providesTags: ['Company' as const],
  },
  getCompanyById: {
    query: (uuid: string) => `companies/${uuid}`,
    providesTags: (_result: unknown, _error: unknown, uuid: string) => [
      { type: 'Company' as const, uuid },
    ],
  },
  createCompany: {
    mutation: {
      query: (company: Partial<Company>) => ({
        url: 'companies',
        method: 'POST',
        body: snakeCaseKeys(company, { deep: true }),
      }),
      invalidatesTags: ['Company' as const, 'Analytics' as const],
    },
  },
  updateCompany: {
    mutation: {
      query: ({ uuid, ...updates }: Partial<Company> & { uuid: string }) => ({
        url: `companies/${uuid}`,
        method: 'PATCH',
        body: snakeCaseKeys(updates, { deep: true }),
      }),
      invalidatesTags: (
        _result: unknown,
        _error: unknown,
        { uuid }: { uuid: string }
      ) => [{ type: 'Company' as const, uuid }, 'Analytics' as const],
    },
  },
  getCompanyStats: {
    query: (uuid: string) => `companies/${uuid}/stats`,
  },

  // Team endpoints
  getTeams: {
    query: (companyUuid?: string) =>
      companyUuid ? `teams?company_uuid=${companyUuid}` : 'teams',
    providesTags: ['Team' as const],
  },
  getTeamById: {
    query: (uuid: string) => `teams/${uuid}`,
    providesTags: (_result: unknown, _error: unknown, uuid: string) => [
      { type: 'Team' as const, uuid },
    ],
  },
  createTeam: {
    mutation: {
      query: (team: Partial<Team>) => ({
        url: 'teams',
        method: 'POST',
        body: snakeCaseKeys(team, { deep: true }),
      }),
      invalidatesTags: ['Team' as const, 'Analytics' as const],
    },
  },
  updateTeam: {
    mutation: {
      query: ({ uuid, ...updates }: Partial<Team> & { uuid: string }) => ({
        url: `teams/${uuid}`,
        method: 'PATCH',
        body: snakeCaseKeys(updates, { deep: true }),
      }),
      invalidatesTags: (
        _result: unknown,
        _error: unknown,
        { uuid }: { uuid: string }
      ) => [{ type: 'Team' as const, uuid }, 'Analytics' as const],
    },
  },
  addTeamMember: {
    mutation: {
      query: ({ teamUuid, userId }: { teamUuid: string; userId: string }) => ({
        url: `teams/${teamUuid}/members/${userId}`,
        method: 'POST',
      }),
      invalidatesTags: (
        _result: unknown,
        _error: unknown,
        { teamUuid }: { teamUuid: string }
      ) => [{ type: 'Team' as const, uuid: teamUuid }, 'Analytics' as const],
    },
  },
  removeTeamMember: {
    mutation: {
      query: ({ teamUuid, userId }: { teamUuid: string; userId: string }) => ({
        url: `teams/${teamUuid}/members/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (
        _result: unknown,
        _error: unknown,
        { teamUuid }: { teamUuid: string }
      ) => [{ type: 'Team' as const, uuid: teamUuid }, 'Analytics' as const],
    },
  },

  // Company User endpoints
  getCompanyUsers: {
    query: ({
      companyUuid,
      page = 1,
      perPage = 20,
      teamUuid,
      search,
      role,
      isActive,
    }: {
      companyUuid: string;
      page?: number;
      perPage?: number;
      teamUuid?: string;
      search?: string;
      role?: string;
      isActive?: boolean;
    }) => {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });

      if (teamUuid) params.append('team_uuid', teamUuid);
      if (search) params.append('search', search);
      if (role) params.append('role', role);
      if (isActive !== undefined)
        params.append('is_active', isActive.toString());

      return `users/${companyUuid}/users?${params.toString()}`;
    },
    providesTags: (
      _result: unknown,
      _error: unknown,
      { companyUuid }: { companyUuid: string }
    ) => [{ type: 'Company' as const, uuid: companyUuid }, 'User' as const],
  },
  inviteUsersToCompany: {
    mutation: {
      query: ({
        companyUuid,
        invitations,
      }: {
        companyUuid: string;
        invitations: Array<{
          email: string;
          name: string;
          role: string;
          teamUuids?: string[];
          sendWelcomeEmail?: boolean;
          customMessage?: string;
        }>;
      }) => ({
        url: `users/${companyUuid}/users/invite?send_email=true`,
        method: 'POST',
        body: snakeCaseKeys(invitations, { deep: true }),
      }),
      invalidatesTags: (
        _result: unknown,
        _error: unknown,
        { companyUuid }: { companyUuid: string }
      ) => [
        { type: 'Company' as const, uuid: companyUuid },
        'User' as const,
        'Analytics' as const,
      ],
    },
  },
  updateUserRole: {
    mutation: {
      query: ({
        userUuid,
        role,
        reason,
      }: {
        userUuid: string;
        role: string;
        reason?: string;
      }) => ({
        url: `users/${userUuid}/role`,
        method: 'PATCH',
        body: { role, reason },
      }),
      invalidatesTags: [
        'User' as const,
        'Company' as const,
        'Analytics' as const,
      ],
    },
  },
  bulkUserOperations: {
    mutation: {
      query: ({
        companyUuid,
        operation,
        userUuids,
        ...params
      }: {
        companyUuid: string;
        operation: string;
        userUuids: string[];
        role?: string;
        reason?: string;
      }) => ({
        url: `users/${companyUuid}/users/bulk`,
        method: 'POST',
        body: snakeCaseKeys(
          { operation, user_uuids: userUuids, ...params },
          { deep: true }
        ),
      }),
      invalidatesTags: (
        _result: unknown,
        _error: unknown,
        { companyUuid }: { companyUuid: string }
      ) => [
        { type: 'Company' as const, uuid: companyUuid },
        'User' as const,
        'Analytics' as const,
      ],
    },
  },
  resendUserInvitation: {
    mutation: {
      query: (userUuid: string) => ({
        url: `admin/onboarding/users/${userUuid}/resend-invitation`,
        method: 'POST',
      }),
      invalidatesTags: ['User' as const],
    },
  },
  deactivateUser: {
    mutation: {
      query: ({ userUuid, reason }: { userUuid: string; reason?: string }) => ({
        url: `users/${userUuid}/deactivate`,
        method: 'POST',
        body: {
          is_active: false,
          ...(reason ? { reason } : {}),
        },
      }),
      invalidatesTags: [
        'User' as const,
        'Company' as const,
        'Analytics' as const,
      ],
    },
  },
  activateUser: {
    mutation: {
      query: (userUuid: string) => ({
        url: `users/${userUuid}/reactivate`,
        method: 'POST',
      }),
      invalidatesTags: [
        'User' as const,
        'Company' as const,
        'Analytics' as const,
      ],
    },
  },
  updateUserDisclosureRequired: {
    mutation: {
      query: ({
        userUuid,
        disclosureRequired,
      }: {
        userUuid: string;
        disclosureRequired: boolean;
      }) => ({
        url: `users/${userUuid}/disclosure-required`,
        method: 'PATCH',
        body: { disclosure_required: disclosureRequired },
      }),
      invalidatesTags: ['User' as const, 'Company' as const],
    },
  },
  updateCompanyDisclosureRequired: {
    mutation: {
      query: ({
        companyUuid,
        disclosureRequired,
      }: {
        companyUuid: string;
        disclosureRequired: boolean;
      }) => ({
        url: `companies/${companyUuid}/disclosure-required`,
        method: 'POST',
        body: { disclosure_required: disclosureRequired },
      }),
      invalidatesTags: (
        _result: unknown,
        _error: unknown,
        { companyUuid }: { companyUuid: string }
      ) => [{ type: 'Company' as const, uuid: companyUuid }, 'User' as const],
    },
  },

  // Analytics endpoint - DEPRECATED: Use the new analytics endpoints in analytics.ts
  getRoleBasedDashboard: {
    query: () => 'analytics/dashboard',
    providesTags: ['Analytics' as const],
  },
};
